// Figma: "Loyalty Cloud" file, node 18428:60586 ("Dialog" — lease date range calendar
// popover), opened from the "Lease date" field in ./EditResidentDialog.js. No date-range
// picker library is installed (AlertsSection.js's header comment notes @mui/x-date-pickers
// was deliberately dropped for that pass), so this is a small custom range calendar built
// on dayjs (already a dependency) matching the Figma cell-by-cell instead.
import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {Box, ButtonBase, Popover, Stack, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';
import chevronUpIcon from '../../../assets/residentDetail/chevron-up-icon.svg';
import chevronDownIcon from '../../../assets/residentDetail/chevron-down-icon.svg';

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const CELL_SIZE = 32;
const CELL_WIDTH = 37;

const buildMonthGrid = (monthStart) => {
    const startOfMonth = monthStart.startOf('month');
    const gridStart = startOfMonth.startOf('week');
    const days = [];
    for (let i = 0; i < 42; i += 1) {
        days.push(gridStart.add(i, 'day'));
    }
    return days;
};

const DayCell = ({day, inCurrentMonth, isStart, isEnd, inRange, onClick}) => {
    const isEndpoint = isStart || isEnd;
    return (
        <Box sx={{position: 'relative', width: CELL_WIDTH, height: CELL_SIZE}}>
            {inRange && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        left: isStart ? '50%' : 0,
                        right: isEnd ? '50%' : 0,
                        backgroundColor: tokens.color.neutral200
                    }}
                />
            )}
            <ButtonBase
                onClick={() => onClick(day)}
                sx={{
                    position: 'relative',
                    width: CELL_SIZE,
                    height: CELL_SIZE,
                    borderRadius: '4px',
                    backgroundColor: isEndpoint ? tokens.color.neutral950 : 'transparent'
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: isEndpoint ? tokens.color.white : inCurrentMonth ? tokens.color.neutral950 : tokens.color.neutral500
                    }}
                >
                    {day.date()}
                </Typography>
            </ButtonBase>
        </Box>
    );
};

DayCell.propTypes = {
    day: PropTypes.object.isRequired,
    inCurrentMonth: PropTypes.bool,
    isStart: PropTypes.bool,
    isEnd: PropTypes.bool,
    inRange: PropTypes.bool,
    onClick: PropTypes.func.isRequired
};

const LeaseDateRangePicker = ({anchorEl, open, onClose, startDate, endDate, onChange}) => {
    const initialMonth = (startDate ? dayjs(startDate) : dayjs()).startOf('month');
    const [visibleMonth, setVisibleMonth] = React.useState(initialMonth);

    React.useEffect(() => {
        if (open) {
            setVisibleMonth((startDate ? dayjs(startDate) : dayjs()).startOf('month'));
        }
    }, [open, startDate]);

    const start = startDate ? dayjs(startDate) : null;
    const end = endDate ? dayjs(endDate) : null;

    const handleDayClick = (day) => {
        if (!start || (start && end)) {
            onChange({start: day, end: null});
            return;
        }
        if (day.isBefore(start, 'day')) {
            onChange({start: day, end: null});
            return;
        }
        onChange({start, end: day});
    };

    const days = buildMonthGrid(visibleMonth);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            transformOrigin={{vertical: 'top', horizontal: 'left'}}
            PaperProps={{
                sx: {
                    width: '280px',
                    maxHeight: '764px',
                    borderRadius: '12px',
                    border: `1px solid ${tokens.color.neutral200}`,
                    px: '12px',
                    pt: '9px',
                    pb: '12px'
                }
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{width: '100%'}}>
                <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral950, flex: 1}}>
                    {visibleMonth.format('MMMM YYYY')}
                </Typography>
                <ButtonBase
                    onClick={() => setVisibleMonth((m) => m.subtract(1, 'month'))}
                    sx={{width: 32, height: 32, borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                >
                    <Box component="img" src={chevronUpIcon} alt="Previous" sx={{width: 18, height: 18, transform: 'rotate(-90deg)'}} />
                </ButtonBase>
                <ButtonBase
                    onClick={() => setVisibleMonth((m) => m.add(1, 'month'))}
                    sx={{width: 32, height: 32, borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                >
                    <Box component="img" src={chevronDownIcon} alt="Next" sx={{width: 18, height: 18, transform: 'rotate(-90deg)'}} />
                </ButtonBase>
            </Stack>

            <Stack direction="row" sx={{width: '100%', mt: 1}}>
                {WEEKDAY_LABELS.map((label, i) => (
                    <Box key={`${label}-${i}`} sx={{width: CELL_WIDTH, textAlign: 'center'}}>
                        <Typography variant="caption" sx={{color: tokens.color.neutral600}}>
                            {label}
                        </Typography>
                    </Box>
                ))}
            </Stack>

            <Box sx={{pt: '5px'}}>
                {Array.from({length: 6}).map((_, rowIdx) => (
                    <Stack key={rowIdx} direction="row" justifyContent="space-between" sx={{width: '100%'}}>
                        {days.slice(rowIdx * 7, rowIdx * 7 + 7).map((day) => (
                            <DayCell
                                key={day.format('YYYY-MM-DD')}
                                day={day}
                                inCurrentMonth={day.isSame(visibleMonth, 'month')}
                                isStart={Boolean(start) && day.isSame(start, 'day')}
                                isEnd={Boolean(end) && day.isSame(end, 'day')}
                                inRange={Boolean(start) && Boolean(end) && day.isAfter(start, 'day') && day.isBefore(end, 'day')}
                                onClick={handleDayClick}
                            />
                        ))}
                    </Stack>
                ))}
            </Box>
        </Popover>
    );
};

LeaseDateRangePicker.propTypes = {
    anchorEl: PropTypes.any,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onChange: PropTypes.func.isRequired
};

export default LeaseDateRangePicker;
